import { config } from "@/config/env";
import type { LoginResponse, LoginSuccessResponse } from "./types";

const API_BASE_URL = config.NEXT_PUBLIC_AI_DASHBOARD_API_URL;

export async function signIn(username: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.errorMessage || "Login failed");
    }

    const data = await response.json();

    if (data.requiresTwoFactor) {
        return {
            requiresTwoFactor: true,
            mfaSessionToken: data.mfaSessionToken,
            twoFactorMethods: data.twoFactorMethods ?? ["email"],
            userId: data.userId ?? "",
            hints: data.hints ?? {},
        };
    }

    return { accessToken: data.accessToken, authData: data.authData, mapToken: data.mapToken, profileData: data.profileData, userPreferences: data.userPreferences };
}

export async function verifyOtp(mfaSessionToken: string, code: string): Promise<LoginSuccessResponse> {
    const response = await fetch(`${API_BASE_URL}/login/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfaSessionToken, code }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.errorMessage || "Verification failed");
    }

    const { accessToken, authData, mapToken, profileData, userPreferences } = await response.json();
    return { accessToken, authData, mapToken, profileData, userPreferences };
}

interface SendResetEmailParams {
    userEmail: string;
}

export async function sendResetEmail(params: SendResetEmailParams): Promise<{ ok: boolean }> {
    const response = await fetch(`${API_BASE_URL}/auth/send-reset-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error("Error sending reset email:", errorBody);
        return { ok: false };
    }

    return { ok: true };
}

export async function waitForOtpReady(userId: string, maxAttempts = 8, intervalMs = 500): Promise<void> {
    for (let i = 0; i < maxAttempts; i++) {
        const res = await fetch(`${API_BASE_URL}/otp/pending?userId=${encodeURIComponent(userId)}`);
        if (res.ok) {
            const data = await res.json();
            if (data.ready) return;
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    throw new Error("OTP not ready. Please try again.");
}

export async function sendOtpCode(userId: string, otp_method: "email" | "sms"): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/otp/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, otp_method }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to send verification code");
    }
}

export async function ssoLogin(code: string, redirectUri: string): Promise<LoginSuccessResponse> {
    const response = await fetch(`${API_BASE_URL}/login/sso`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, redirectUri }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.errorMessage || "SSO login failed");
    }

    const { accessToken, authData, mapToken, profileData, userPreferences } = await response.json();
    return { accessToken, authData, mapToken, profileData, userPreferences };
}
